import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText,
  ChevronLeft,
  DollarSign,
  Package,
  Clock,
  CheckCircle2,
  ShoppingBag,
  Loader2,
} from "lucide-react";
import { toast } from "react-toastify";
import quoteService from "../../api/QuoteService";
import Footer from "../../components/landing/Footer";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-800", icon: Clock },
  quoted: { label: "Quoted", color: "bg-blue-100 text-blue-800", icon: DollarSign },
  accepted: { label: "Accepted", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800", icon: Clock },
};

export default function MyQuoteRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      try {
        const result = await quoteService.getMyQuoteRequests();
        const data = result?.data ?? (Array.isArray(result?.data) ? result.data : []);
        setRequests(Array.isArray(data) ? data : []);
      } catch (err) {
        setRequests([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, []);

  const handlePayNow = (req) => {
    if (req.status !== "quoted" || req.quoted_amount == null) return;
    navigate("/checkout", {
      state: {
        fromQuote: true,
        quoteRequestId: req.id,
        policySlug: req.policy_slug,
        coverageDurationIndex: req.coverage_duration_index,
        amount: req.quoted_amount,
        policyName: req.policy_name,
        packageName: req.package_name || req.product_name,
        originalPolicy: req.policy_data || null,
      },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      <div className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <motion.button
          onClick={() => navigate(-1)}
          className="flex gap-2 items-center mb-6 text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          <ChevronLeft size={18} />
          Back
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex gap-3 items-center mb-2">
            <div className="flex justify-center items-center w-12 h-12 bg-amber-100 rounded-xl">
              <FileText className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Quote Requests</h1>
              <p className="text-sm text-gray-600">Track your quote requests and pay when you receive a quote</p>
            </div>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
          </div>
        ) : requests.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-16 text-center bg-white rounded-xl border border-gray-200 shadow-sm"
          >
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No quote requests</h3>
            <p className="text-sm text-gray-600 max-w-md mx-auto mb-6">
              When you request a quote for a quote-based insurance package, your requests will appear here. Once the provider sends a quoted amount, you can pay to complete the purchase.
            </p>
            <button
              type="button"
              onClick={() => navigate("/insurance")}
              className="px-6 py-3 text-sm font-semibold text-white bg-amber-500 rounded-lg hover:bg-amber-600 transition-colors"
            >
              Browse Insurance
            </button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {requests.map((req) => {
              const statusInfo = STATUS_CONFIG[req.status || "pending"] || STATUS_CONFIG.pending;
              const Icon = statusInfo.icon;
              const isQuoted = req.status === "quoted" && req.quoted_amount != null && req.quoted_amount > 0;

              return (
                <motion.div
                  key={req.id || req.slug || Math.random()}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex gap-2 items-center flex-wrap">
                        <Package className="w-5 h-5 text-amber-600 shrink-0" />
                        <span className="font-semibold text-gray-900">
                          {req.package_name || req.product_name || "Package"}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${statusInfo.color}`}>
                          <Icon className="w-3 h-3" />
                          {statusInfo.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{req.policy_name || "Policy"}</p>
                      {isQuoted && (
                        <p className="mt-2 text-lg font-bold text-green-700">
                          Quoted: ₵{Number(req.quoted_amount).toFixed(2)}
                        </p>
                      )}
                    </div>
                    {isQuoted && (
                      <button
                        type="button"
                        onClick={() => handlePayNow(req)}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shrink-0"
                      >
                        <ShoppingBag size={18} />
                        Pay Now
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
